(() => {
  'use strict';
  if (window.__jrCollectionTabNav) return;
  window.__jrCollectionTabNav = true;

  const cache = new Map();
  const inflight = new Map();
  const pinnedKeys = new Set();
  const MAX_CACHE_BYTES = 50 * 1024 * 1024;
  const prefetched = new Set();

  let cacheBytes = 0;
  let navAbort = null;
  let navGen = 0;
  let navigating = false;
  let gridSectionId = null;
  let heroSectionId = null;

  const normalizePath = (pathname) => {
    const path = (pathname || '').replace(/\/+$/, '');
    return path || '/';
  };

  const resolveGridSectionId = () => {
    if (gridSectionId) return gridSectionId;
    const el = document.querySelector('collection-info[data-section]');
    gridSectionId = el?.dataset.section || null;
    return gridSectionId;
  };

  const resolveHeroSectionId = () => {
    if (heroSectionId !== null) return heroSectionId || null;
    const hero = document.querySelector('.hero--collection');
    const wrapper = hero?.closest('[id^="shopify-section-"]');
    heroSectionId = wrapper ? wrapper.id.replace('shopify-section-', '') : '';
    return heroSectionId || null;
  };

  const buildFetchUrl = (pathname, sectionId) => {
    const url = new URL(normalizePath(pathname), window.location.origin);
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('section_id', sectionId);
    return url.pathname + url.search;
  };

  const buildHistoryUrl = (pathname) => {
    const params = new URLSearchParams();
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) params.set('preview_theme_id', preview);
    const qs = params.toString();
    return normalizePath(pathname) + (qs ? `?${qs}` : '');
  };

  const canClientNav = () => !!resolveGridSectionId();

  const isCollectionTabLink = (anchor) => {
    if (!anchor?.href) return false;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return false;
    try {
      const u = new URL(anchor.href, window.location.origin);
      if (u.origin !== window.location.origin) return false;
      return /^\/collections\/[^/]+\/?$/.test(u.pathname);
    } catch {
      return false;
    }
  };

  const extractTitle = (doc) =>
    doc.querySelector('collection-info')?.getAttribute('data-collection-title') ||
    doc.querySelector('.collection__grid-title h2')?.textContent?.trim() ||
    doc.querySelector('.lp-breadcrumbs span:last-of-type')?.textContent?.trim() ||
    doc.querySelector('.collection__grid-header--meta h1')?.textContent?.trim() ||
    '';

  const parseSectionEntry = (html, sectionId) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const gridId = resolveGridSectionId();
    const requireGrid = gridId && sectionId === gridId;
    const isValid = !requireGrid || !!doc.querySelector('collection-info[data-section]');
    return {
      html,
      doc,
      title: extractTitle(doc),
      isValid,
      size: html.length,
    };
  };

  const trimCache = () => {
    while (cacheBytes > MAX_CACHE_BYTES && cache.size > pinnedKeys.size) {
      const evictKey = [...cache.keys()].find((key) => !pinnedKeys.has(key));
      if (!evictKey) break;
      const entry = cache.get(evictKey);
      cacheBytes -= entry?.size || 0;
      cache.delete(evictKey);
    }
  };

  const pinCacheKey = (key) => {
    pinnedKeys.add(key);
  };

  const isCollectionTabPath = (pathname) => /^\/collections\/[^/]+\/?$/.test(normalizePath(pathname));

  const fetchSection = (pathname, sectionId, signal, { pin = false } = {}) => {
    const key = buildFetchUrl(pathname, sectionId);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (inflight.has(key)) return inflight.get(key);

    const request = fetch(key, { signal })
      .then((res) => {
        if (!res.ok) throw new Error('section fetch failed');
        return res.text();
      })
      .then((html) => {
        if (!html?.trim()) throw new Error('empty section response');
        const entry = parseSectionEntry(html, sectionId);
        if (!entry.isValid) throw new Error('invalid section html');
        cache.set(key, entry);
        cacheBytes += entry.size;
        if (pin || isCollectionTabPath(pathname)) pinCacheKey(key);
        trimCache();
        return entry;
      })
      .finally(() => inflight.delete(key));

    inflight.set(key, request);
    return request;
  };

  const removeDuplicateSections = (sectionId) => {
    const selector = `#shopify-section-${CSS.escape(sectionId)}`;
    document.querySelectorAll(selector).forEach((node, index) => {
      if (index > 0) node.remove();
    });
    return document.querySelector(selector);
  };

  const swapSection = (sectionId, entry) => {
    if (!entry?.isValid || !entry.doc) return false;

    const liveRoot = entry.doc.querySelector('.shopify-section') || entry.doc.body.firstElementChild;
    if (!liveRoot) return false;

    const src = liveRoot.cloneNode(true);
    const sectionDomId = `shopify-section-${sectionId}`;
    src.id = sectionDomId;

    const dest = document.getElementById(sectionDomId);
    if (!dest) return false;

    dest.replaceWith(src);
    removeDuplicateSections(sectionId);
    return true;
  };

  const updateTitle = (collectionTitle) => {
    if (!collectionTitle) return;
    const parts = document.title.split(' – ');
    const shopSuffix = parts.length > 1 ? parts[parts.length - 1] : '';
    document.title = shopSuffix ? `${collectionTitle} – ${shopSuffix}` : collectionTitle;
  };

  const updateCanonical = (pathname) => {
    const url = `${window.location.origin}${normalizePath(pathname)}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', url);
  };

  const updateSocialMeta = (title, pathname) => {
    const url = `${window.location.origin}${normalizePath(pathname)}`;
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', url);
    if (title) {
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title);
    }
  };

  const resetListTracking = (root) => {
    const scope = root || document;
    scope.querySelectorAll('[data-gtm-list-viewed], [data-gtm-observer-attached]').forEach((el) => {
      el.removeAttribute('data-gtm-list-viewed');
      el.removeAttribute('data-gtm-observer-attached');
    });
    const dlView = document.querySelector('data-layer-view-item-list');
    if (dlView && typeof dlView.observeProductLists === 'function') {
      dlView.observeProductLists();
    }
  };

  const trackCollectionPageView = (pathname, title) => {
    window.dataLayer = window.dataLayer || [];
    const handle = pathname.match(/\/collections\/([^/?#]+)/)?.[1] || 'unknown';
    window.dataLayer.push({
      event: 'virtual_page_view',
      page_path: normalizePath(pathname),
      page_title: title || document.title,
      page_type: 'collection',
      collection_handle: handle,
    });
  };

  const closeFilterDrawer = () => {
    try {
      const drawer = window.Alpine?.store?.('drawer');
      if (drawer) {
        drawer.open = false;
        if (typeof drawer.DrawerClose === 'function') {
          drawer.DrawerClose();
          return;
        }
      }
    } catch (e) {}
    document.body.classList.remove('filters-drawer-open');
    document.documentElement.classList.remove('overflow-hidden');
  };

  const beginTabNav = () => {
    closeFilterDrawer();
    document.body.classList.add('collection-tab-nav-active');
  };

  const endTabNav = () => {
    document.body.classList.remove('collection-tab-nav-active');
    closeFilterDrawer();
  };

  const setActiveTab = (pathname) => {
    const activePath = normalizePath(pathname);
    document.querySelectorAll('[data-lp-nav-links] a, [data-cclp-nav] a').forEach((anchor) => {
      const hrefPath = normalizePath(new URL(anchor.href, window.location.origin).pathname);
      const isActive = hrefPath === activePath;
      const li = anchor.closest('li');
      if (li) li.classList.toggle('active', isActive);
      anchor.classList.toggle('is-active', isActive);
    });
  };

  const showLoading = (id) => {
    const section = document.getElementById(`shopify-section-${id}`);
    if (section) section.classList.add('collection-tab-nav-loading');
    const cclpOverlay = document.getElementById(`loading-overlay-${id}`);
    if (cclpOverlay) cclpOverlay.style.display = 'flex';
  };

  const hideLoading = (id) => {
    const section = document.getElementById(`shopify-section-${id}`);
    if (section) section.classList.remove('collection-tab-nav-loading');
    const cclpOverlay = document.getElementById(`loading-overlay-${id}`);
    if (cclpOverlay) cclpOverlay.style.display = '';
  };

  const revealProductStars = (root) => {
    if (!root) return;
    root.querySelectorAll('.product__card-stars, .lp-product-card__reviews').forEach((wrap) => {
      const countEl = wrap.querySelector('.junip-product-summary-review-count');
      if (!countEl) return;
      if (!(countEl.textContent || countEl.innerHTML || '').trim()) return;
      wrap.style.opacity = '1';
    });
  };

  const reloadJunipStars = (root) => {
    if (window.junip && typeof window.junip.refreshWidgets === 'function') {
      try {
        window.junip.refreshWidgets();
      } catch (e) {}
    }
    if (window.junip && typeof window.junip.storefrontStarsLoader === 'function') {
      try {
        window.junip.storefrontStarsLoader();
      } catch (e) {}
    } else if (window.junipLoader && typeof window.junipLoader.load === 'function') {
      try {
        window.junipLoader.load();
      } catch (e) {}
    }
    revealProductStars(root);
  };

  const upgradeSectionElements = (sectionEl) => {
    if (!sectionEl || typeof customElements.upgrade !== 'function') return;
    customElements.upgrade(sectionEl);
    sectionEl.querySelectorAll('cclp-plp-grid, cclp-plp-flat-swap, cclp-plp-quickshop').forEach((node) => {
      customElements.upgrade(node);
    });
  };

  const afterPageUpdate = (sectionEl, pathname, collectionTitle) => {
    upgradeSectionElements(sectionEl);
    document.dispatchEvent(new CustomEvent('theme:page:load', { detail: { sectionEl } }));
    requestAnimationFrame(() => {
      reloadJunipStars(sectionEl);
      resetListTracking(sectionEl);
      trackCollectionPageView(pathname, collectionTitle);
      setTimeout(() => reloadJunipStars(sectionEl), 300);
    });
  };

  const navigateTo = async (pathname, { push = true } = {}) => {
    const targetPath = normalizePath(pathname);
    if (targetPath === normalizePath(window.location.pathname)) return;

    const currentGridId = resolveGridSectionId();
    if (!currentGridId || !canClientNav()) {
      window.location.href = buildHistoryUrl(targetPath);
      return;
    }

    navAbort?.abort();
    navAbort = new AbortController();
    const { signal } = navAbort;
    const gen = ++navGen;

    beginTabNav();

    const currentHeroId = resolveHeroSectionId();
    navigating = true;
    showLoading(currentGridId);

    try {
      const requests = [fetchSection(targetPath, currentGridId, signal, { pin: true })];
      if (currentHeroId) requests.push(fetchSection(targetPath, currentHeroId, signal, { pin: true }));
      const [gridEntry, heroEntry] = await Promise.all(requests);

      if (gen !== navGen || signal.aborted) return;

      closeFilterDrawer();

      const sectionRoot = document.getElementById(`shopify-section-${currentGridId}`);
      sectionRoot?.querySelectorAll('.breakout-inserted').forEach((el) => el.remove());
      removeDuplicateSections(currentGridId);

      if (currentHeroId && heroEntry) swapSection(currentHeroId, heroEntry);
      if (!swapSection(currentGridId, gridEntry)) throw new Error('grid swap failed');

      const sectionEl = document.getElementById(`shopify-section-${currentGridId}`);
      const historyUrl = buildHistoryUrl(targetPath);

      if (push) history.pushState({ jrCollectionTab: true }, '', historyUrl);

      setActiveTab(targetPath);
      updateTitle(gridEntry.title);
      updateCanonical(targetPath);
      updateSocialMeta(gridEntry.title, targetPath);
      afterPageUpdate(sectionEl, targetPath, gridEntry.title);
    } catch (err) {
      if (err?.name === 'AbortError' || signal.aborted) return;
      window.location.href = buildHistoryUrl(targetPath);
    } finally {
      if (gen === navGen) {
        hideLoading(currentGridId);
        navigating = false;
        endTabNav();
      }
    }
  };

  const prefetch = (pathname) => {
    if (!canClientNav()) return;
    const targetPath = normalizePath(pathname);
    if (prefetched.has(targetPath)) return;
    prefetched.add(targetPath);

    const gridId = resolveGridSectionId();
    const heroId = resolveHeroSectionId();
    if (!gridId) return;

    const pin = true;
    fetchSection(targetPath, gridId, undefined, { pin }).catch(() => {});
    if (heroId) fetchSection(targetPath, heroId, undefined, { pin }).catch(() => {});
  };

  const prefetchFromAnchor = (anchor) => {
    if (!anchor || !isCollectionTabLink(anchor)) return;
    prefetch(new URL(anchor.href, window.location.origin).pathname);
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = event.target.closest('[data-lp-nav-links] a, [data-cclp-nav] a');
    if (!anchor || !isCollectionTabLink(anchor)) return;
    if (!canClientNav()) return;

    event.preventDefault();
    navigateTo(new URL(anchor.href, window.location.origin).pathname);
  });

  document.addEventListener('pointerenter', (event) => {
    prefetchFromAnchor(event.target.closest?.('[data-lp-nav-links] a, [data-cclp-nav] a'));
  }, true);

  document.addEventListener('touchstart', (event) => {
    prefetchFromAnchor(event.target.closest?.('[data-lp-nav-links] a, [data-cclp-nav] a'));
  }, { capture: true, passive: true });

  window.addEventListener('popstate', () => {
    navigateTo(window.location.pathname, { push: false });
  });

  const idlePrefetchTabs = () => {
    const anchors = document.querySelectorAll('[data-cclp-nav] a, [data-lp-nav-links] a');
    anchors.forEach((anchor, index) => {
      if (!isCollectionTabLink(anchor)) return;
      const path = normalizePath(new URL(anchor.href, window.location.origin).pathname);
      setTimeout(() => prefetch(path), index * 300);
    });
  };

  const seedCurrentTabCache = () => {
    const gridId = resolveGridSectionId();
    const heroId = resolveHeroSectionId();
    const path = normalizePath(window.location.pathname);
    if (!gridId || !isCollectionTabPath(path)) return;

    pinCacheKey(buildFetchUrl(path, gridId));
    if (heroId) pinCacheKey(buildFetchUrl(path, heroId));
  };

  seedCurrentTabCache();

  if ('requestIdleCallback' in window) {
    requestIdleCallback(idlePrefetchTabs, { timeout: 3000 });
  } else {
    setTimeout(idlePrefetchTabs, 1500);
  }
})();
